import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Chip,
    Tooltip,
    Button,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
} from "@mui/material";
import {
    Close as CloseIcon,
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
    History as HistoryIcon,
    ShoppingBag as OrderIcon,
} from "@mui/icons-material";
import { Customer, Order, OrderItem } from "../../types";
import { orderService } from "../../services/orderService";
import { formatCurrencyBDT } from "../../utils/currency";
import { getCustomerFullName } from "../../utils/customerUtils";

interface OrderWithItems {
    order: Order;
    items: OrderItem[];
}

interface CustomerOrderHistoryDialogProps {
    customer: Customer;
    open: boolean;
    onClose: () => void;
    /** When provided, "Add" buttons appear on each item. Called with the item to add. */
    onAddItem?: (productId: string, productName: string, sku: string, unitPrice: number) => void;
}

function formatOrderDate(dateStr: string | undefined): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

const statusColor: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
    DELIVERED: "success",
    CONFIRMED: "primary",
    SHIPPED: "primary",
    PENDING: "warning",
    CANCELLED: "error",
};

export const CustomerOrderHistoryDialog: React.FC<CustomerOrderHistoryDialogProps> = ({
    customer,
    open,
    onClose,
    onAddItem,
}) => {
    const [ordersWithItems, setOrdersWithItems] = useState<OrderWithItems[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [expandedOrder, setExpandedOrder] = useState<string | false>(false);

    // Fetch on open
    useEffect(() => {
        if (!open) return;
        setSearch("");
        setExpandedOrder(false);
        setError(null);

        const load = async () => {
            setLoading(true);
            try {
                const resp = await orderService.getOrders({
                    customer_id: customer.customer_id,
                    limit: 30,
                });
                const orders = resp.items;

                if (orders.length === 0) {
                    setOrdersWithItems([]);
                    setLoading(false);
                    return;
                }

                // Expand first order by default
                setExpandedOrder(orders[0].order_id);

                // Fetch items for all orders in parallel
                const itemsResults = await Promise.all(
                    orders.map((o) =>
                        orderService.getOrderItems(o.order_id).catch(() => [] as OrderItem[]),
                    ),
                );

                setOrdersWithItems(
                    orders.map((order, i) => ({ order, items: itemsResults[i] })),
                );
            } catch (err: any) {
                setError(err.response?.data?.detail || "Failed to load order history.");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, customer.customer_id]);

    // Flat list of all items with their order reference (for search mode)
    const allItems = useMemo(() => {
        return ordersWithItems.flatMap(({ order, items }) =>
            items.map((item) => ({ item, order })),
        );
    }, [ordersWithItems]);

    const trimmedSearch = search.trim().toLowerCase();

    const filteredItems = useMemo(() => {
        if (!trimmedSearch) return allItems;
        return allItems.filter(({ item }) => {
            const name = item.product?.name?.toLowerCase() ?? "";
            const sku = item.product?.sku?.toLowerCase() ?? "";
            return name.includes(trimmedSearch) || sku.includes(trimmedSearch);
        });
    }, [allItems, trimmedSearch]);

    const customerName = getCustomerFullName(customer);
    const isSearching = trimmedSearch.length > 0;

    function renderAddButton(item: OrderItem) {
        if (!onAddItem) return null;
        const pid = item.product_id;
        const name = item.product?.name ?? "Unknown product";
        const sku = item.product?.sku ?? "";
        const price = item.unit_price;
        return (
            <Tooltip title={`Add at ${formatCurrencyBDT(price)}`}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => onAddItem(pid, name, sku, price)}
                    sx={{ ml: 1, flexShrink: 0, whiteSpace: "nowrap" }}
                >
                    {formatCurrencyBDT(price)}
                </Button>
            </Tooltip>
        );
    }

    function renderItemRow(item: OrderItem) {
        return (
            <Box
                key={item.item_id}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 0.75,
                    gap: 1,
                    "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" },
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                        {item.product?.name ?? item.product_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        SKU: {item.product?.sku ?? "—"} &nbsp;·&nbsp;
                        Qty: {item.quantity} &nbsp;·&nbsp;
                        Unit: {formatCurrencyBDT(item.unit_price)} &nbsp;·&nbsp;
                        Total: {formatCurrencyBDT(item.total_price)}
                    </Typography>
                </Box>
                {renderAddButton(item)}
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pb: 1,
                    pr: 6,
                }}
            >
                <HistoryIcon color="action" />
                <Box>
                    <Typography variant="h6" component="span">
                        Order History
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        component="div"
                    >
                        {customerName}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Search bar */}
            <Box sx={{ px: 3, py: 1.5 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search past items by name or SKU…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch("")}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    autoFocus
                />
            </Box>

            <Divider />

            <DialogContent sx={{ p: 0, minHeight: 300 }}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && error && (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                )}

                {!loading && !error && ordersWithItems.length === 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            pt: 6,
                            gap: 1,
                            color: "text.secondary",
                        }}
                    >
                        <OrderIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                        <Typography>No previous orders found for {customerName}.</Typography>
                    </Box>
                )}

                {/* Search results — flat item list */}
                {!loading && !error && isSearching && (
                    <Box sx={{ px: 3, py: 2 }}>
                        {filteredItems.length === 0 ? (
                            <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                                No items matching "{search}" across {ordersWithItems.length} order
                                {ordersWithItems.length !== 1 ? "s" : ""}.
                            </Typography>
                        ) : (
                            <>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mb: 1.5 }}
                                >
                                    {filteredItems.length} item{filteredItems.length !== 1 ? "s" : ""}{" "}
                                    matching "{search}"
                                </Typography>
                                {filteredItems.map(({ item, order }) => (
                                    <Box key={`${order.order_id}-${item.item_id}`}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "space-between",
                                                py: 1,
                                                gap: 1,
                                                "&:not(:last-child)": {
                                                    borderBottom: "1px solid",
                                                    borderColor: "divider",
                                                },
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight="medium" noWrap>
                                                    {item.product?.name ?? item.product_id}
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, flexWrap: "wrap" }}>
                                                    <Chip
                                                        label={order.order_number}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ height: 18, fontSize: "0.68rem" }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatOrderDate(order.order_date)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        · Qty: {item.quantity} · Unit: {formatCurrencyBDT(item.unit_price)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {renderAddButton(item)}
                                        </Box>
                                    </Box>
                                ))}
                            </>
                        )}
                    </Box>
                )}

                {/* Default view — order accordion */}
                {!loading && !error && !isSearching && ordersWithItems.length > 0 && (
                    <Box>
                        {ordersWithItems.map(({ order, items }) => (
                            <Accordion
                                key={order.order_id}
                                expanded={expandedOrder === order.order_id}
                                onChange={(_, expanded) =>
                                    setExpandedOrder(expanded ? order.order_id : false)
                                }
                                disableGutters
                                elevation={0}
                                sx={{
                                    "&:before": { display: "none" },
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ px: 3 }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            flex: 1,
                                            minWidth: 0,
                                            mr: 1,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight="bold">
                                            {order.order_number}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatOrderDate(order.order_date)}
                                        </Typography>
                                        <Chip
                                            label={order.status}
                                            size="small"
                                            color={statusColor[order.status] ?? "default"}
                                            sx={{ height: 20, fontSize: "0.7rem" }}
                                        />
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{ ml: "auto" }}
                                        >
                                            {formatCurrencyBDT(order.total_amount)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {items.length} item{items.length !== 1 ? "s" : ""}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pt: 0, pb: 1.5 }}>
                                    {items.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No items found.
                                        </Typography>
                                    ) : (
                                        items.map((item) => renderItemRow(item))
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};
